import type { ReactNode } from 'react';

import { Badge, Card, CardContent, CardHeader } from '@/components/ui';

import {
  type BadgeVariant,
  subtleStatusBadgeBackground,
} from './badgeVariants';

// The shared drill-down render contract. Every surface (Task-System roll-up,
// human-review request source-context, Assess audit findings) writes a thin pure adapter
// from its own data into a TraceNode — the primitive is data-shape driven and
// imports no domain types, so it is never forked per surface.
export type TraceRef = { label: string; location?: string; href?: string };

export type TraceEvent = {
  id?: string;
  sequence?: number;
  context?: string;
  at?: string;
  title: string;
  detail?: string;
  provenance?: string;
  badge?: { label: string; variant: BadgeVariant };
  refs?: TraceRef[];
};

export type TraceArtifact = {
  label: string;
  value: string;
  href?: string;
  wide?: boolean;
};

export type TraceSecondary = {
  label: string;
  refs?: TraceRef[];
  raw?: unknown;
};

export type TraceNode = {
  title: string;
  metadata: Array<{
    label: string;
    value: string;
    wide?: boolean;
    full?: boolean;
    nowrap?: boolean;
  }>;
  events?: TraceEvent[];
  refs?: TraceRef[];
  artifacts?: TraceArtifact[];
  raw?: unknown;
  secondary?: TraceSecondary;
};

function SectionHeading({ children }: { children: string }) {
  return (
    <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
      {children}
    </h3>
  );
}

export function TraceDrilldown({
  node,
  embedded = false,
  traceActions,
}: {
  node: TraceNode;
  embedded?: boolean;
  traceActions?: ReactNode;
}) {
  const hasEvents = (node.events?.length ?? 0) > 0;
  const hasRefs = (node.refs?.length ?? 0) > 0;
  const hasArtifacts = (node.artifacts?.length ?? 0) > 0;
  const hasRaw = node.raw !== undefined && node.raw !== null;

  return (
    <Card unstyled={embedded}>
      <CardHeader unstyled={embedded}>
        {!embedded && <h2 className="text-base font-semibold">{node.title}</h2>}
        {node.metadata.length > 0 && (
          <dl className="mt-3 grid gap-3 text-sm md:grid-cols-3">
            {node.metadata.map((entry) => (
              <div
                key={entry.label}
                className={`min-w-0 space-y-1 ${
                  entry.full
                    ? 'md:col-span-3'
                    : entry.wide
                      ? 'md:col-span-2'
                      : ''
                }`}
              >
                <dt className="text-muted-foreground text-xs font-medium uppercase">
                  {entry.label}
                </dt>
                <dd
                  data-trace-metadata={entry.label.toLowerCase()}
                  className={
                    entry.nowrap
                      ? 'overflow-x-auto whitespace-nowrap'
                      : 'break-words'
                  }
                >
                  {entry.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CardHeader>
      <CardContent unstyled={embedded} className="space-y-6 pt-4">
        {hasEvents && (
          <section className="space-y-3">
            <SectionHeading>Timeline</SectionHeading>
            <ol className="space-y-3">
              {node.events?.map((event, index) => (
                <li
                  key={`${event.title}-${index}`}
                  id={event.id}
                  data-event-sequence={event.sequence}
                  data-status-tone={event.badge?.variant}
                  className="text-foreground bg-muted/30 rounded-md p-3 text-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div
                      data-event-heading
                      className="flex items-baseline gap-2"
                    >
                      {event.sequence !== undefined && (
                        <span className="text-muted-foreground pr-2 font-mono text-xs">
                          Event #{event.sequence}
                        </span>
                      )}
                      {event.context && (
                        <span
                          data-event-context
                          className="text-muted-foreground text-xs"
                        >
                          {event.context}
                        </span>
                      )}
                      {event.sequence === undefined && event.badge && (
                        <Badge
                          variant={event.badge.variant}
                          className={subtleStatusBadgeBackground(
                            event.badge.variant
                          )}
                        >
                          {event.badge.label}
                        </Badge>
                      )}
                      {event.sequence === undefined && (
                        <span className="font-medium">{event.title}</span>
                      )}
                    </div>
                    {event.sequence !== undefined && event.badge && (
                      <Badge
                        variant={event.badge.variant}
                        className={subtleStatusBadgeBackground(
                          event.badge.variant
                        )}
                      >
                        {event.badge.label}
                      </Badge>
                    )}
                    {event.sequence === undefined && event.at && (
                      <time className="text-muted-foreground text-xs">
                        {event.at}
                      </time>
                    )}
                  </div>
                  {event.sequence !== undefined && event.detail && (
                    <p data-event-detail className="text-muted-foreground mt-2">
                      {event.detail}
                    </p>
                  )}
                  {event.sequence !== undefined &&
                    (event.provenance || event.at) && (
                      <div className="mt-2 flex flex-wrap items-end justify-between gap-2">
                        {event.provenance && (
                          <p
                            data-event-provenance
                            className="text-muted-foreground text-xs"
                          >
                            {event.provenance}
                          </p>
                        )}
                        {event.at && (
                          <time
                            data-event-time
                            className="text-muted-foreground ml-auto text-xs"
                          >
                            {event.at}
                          </time>
                        )}
                      </div>
                    )}
                  {event.sequence === undefined && event.detail && (
                    <p className="text-muted-foreground mt-2">{event.detail}</p>
                  )}
                  {(event.refs?.length ?? 0) > 0 && (
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-violet-300">
                      {event.refs?.map((ref, refIndex) => (
                        <span key={`${ref.label}-${refIndex}`}>
                          {ref.href ? (
                            <a className="underline" href={ref.href}>
                              {ref.label}
                            </a>
                          ) : (
                            ref.label
                          )}
                          {ref.location && <> {ref.location}</>}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>
        )}

        {hasRefs && (
          <section className="space-y-3">
            <SectionHeading>Grounding</SectionHeading>
            <ul className="space-y-2 text-sm">
              {node.refs?.map((ref, index) => (
                <li
                  key={`${ref.label}-${index}`}
                  className="flex flex-wrap items-baseline justify-between gap-2"
                >
                  <span className="min-w-0">
                    {ref.href ? (
                      <a
                        href={ref.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        {ref.label}
                      </a>
                    ) : (
                      ref.label
                    )}
                  </span>
                  {ref.location && (
                    <code className="text-muted-foreground text-xs">
                      {ref.location}
                    </code>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {hasArtifacts && (
          <section>
            <dl
              data-testid="trace-artifacts"
              className="grid gap-3 text-sm md:grid-cols-2 lg:grid-cols-3"
            >
              {node.artifacts?.map((artifact, index) => (
                <div
                  key={`${artifact.label}-${index}`}
                  className={`min-w-0 space-y-1 ${
                    artifact.wide ? 'md:col-span-2 lg:col-span-3' : ''
                  }`}
                >
                  <dt className="text-muted-foreground text-xs font-medium uppercase">
                    {artifact.label}
                  </dt>
                  <dd className="break-words">
                    {artifact.href ? (
                      <a
                        href={artifact.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        {artifact.value}
                      </a>
                    ) : (
                      artifact.value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {node.secondary && (
          <details className="text-sm">
            <summary className="text-muted-foreground cursor-pointer">
              {node.secondary.label}
            </summary>
            {(node.secondary.refs?.length ?? 0) > 0 && (
              <ul className="mt-3 space-y-2 text-sm">
                {node.secondary.refs?.map((ref, index) => (
                  <li
                    key={`${ref.label}-${index}`}
                    className="flex flex-wrap items-baseline justify-between gap-2"
                  >
                    <span>{ref.label}</span>
                    {ref.location && (
                      <code className="text-muted-foreground text-xs">
                        {ref.location}
                      </code>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {node.secondary.raw !== undefined && (
              <pre className="bg-muted mt-3 max-h-64 overflow-auto rounded-md p-3 text-xs">
                {JSON.stringify(node.secondary.raw, null, 2)}
              </pre>
            )}
          </details>
        )}

        {(hasRaw || traceActions) && (
          <section data-trace-section className="space-y-3">
            <SectionHeading>Trace</SectionHeading>
            <div className="flex flex-wrap items-start gap-2">
              {hasRaw && (
                <details className="text-sm">
                  <summary className="border-input bg-background cursor-pointer list-none rounded-md border px-3 py-1.5 font-medium shadow-xs">
                    Raw
                  </summary>
                  <pre className="bg-muted mt-2 max-h-64 overflow-auto rounded-md p-3 text-xs">
                    {JSON.stringify(node.raw, null, 2)}
                  </pre>
                </details>
              )}
              {traceActions}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
